import { useState, useEffect } from 'react';
import { DollarSign, Wrench, TrendingUp, Calendar, Truck, Package, BarChart3, PieChart, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface StatisticsData {
  totalSpent: number;
  partsSpent: number;
  laborSpent: number;
  driverCost: number;
  companyCost: number;
  totalLogs: number;
  avgCostPerLog: number;
  byType: { type: string; count: number; cost: number; driverCost: number; companyCost: number }[];
  byMonth: { month: string; count: number; cost: number; driverCost: number; companyCost: number }[];
  byTruck: { truck: string; count: number; cost: number; driverCost: number; companyCost: number }[];
  topExpenses: { log_number: string; type: string; cost: number; driverCost: number; companyCost: number; date: string }[];
}

export function MaintenanceStatistics() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatisticsData>({
    totalSpent: 0,
    partsSpent: 0,
    laborSpent: 0,
    driverCost: 0,
    companyCost: 0,
    totalLogs: 0,
    avgCostPerLog: 0,
    byType: [],
    byMonth: [],
    byTruck: [],
    topExpenses: [],
  });
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear() - 2, 0, 1).toISOString().split('T')[0], // 2 years ago
    end: new Date().toISOString().split('T')[0], // Today
  });

  useEffect(() => {
    loadStatistics();
  }, [dateRange]);

  async function loadStatistics() {
    try {
      setLoading(true);

      // Build query with date range
      let query = supabase
        .from('maintenance_logs')
        .select(`
          *,
          trucks!maintenance_logs_truck_id_fkey (unit_number),
          trailers!maintenance_logs_trailer_id_fkey (unit_number)
        `);

      if (dateRange.start) {
        query = query.gte('service_date', dateRange.start);
      }
      if (dateRange.end) {
        query = query.lte('service_date', dateRange.end);
      }

      const { data: logs, error } = await query;

      console.log('Maintenance logs query result:', { logs, error, count: logs?.length });
      console.log('Date range:', dateRange);

      if (error) {
        console.error('Query error:', error);
        throw error;
      }

      if (!logs || logs.length === 0) {
        console.log('No maintenance logs found in date range');
        console.log('Setting empty stats');
        toast?.error('No maintenance logs found in the selected date range. Try adjusting the date range or add maintenance logs.');
        setStats({
          totalSpent: 0,
          partsSpent: 0,
          laborSpent: 0,
          driverCost: 0,
          companyCost: 0,
          totalLogs: 0,
          avgCostPerLog: 0,
          byType: [],
          byMonth: [],
          byTruck: [],
          topExpenses: [],
        });
        setLoading(false);
        return;
      }

      // Calculate totals
      const totalSpent = logs.reduce((sum, log) => sum + (log.cost || 0), 0);
      const partsSpent = logs.reduce((sum, log) => sum + (log.parts_cost || 0), 0);
      const laborSpent = logs.reduce((sum, log) => sum + (log.labor_cost || 0), 0);
      const driverCost = logs.reduce((sum, log) => sum + (log.driver_payment_amount || 0), 0);
      const companyCost = logs.reduce((sum, log) => sum + (log.company_cost_amount || 0), 0);
      const totalLogs = logs.length;
      const avgCostPerLog = totalSpent / totalLogs;

      // Group by maintenance type
      const typeMap = new Map<string, { count: number; cost: number; driverCost: number; companyCost: number }>();
      logs.forEach(log => {
        const type = log.maintenance_type || 'Other';
        const existing = typeMap.get(type) || { count: 0, cost: 0, driverCost: 0, companyCost: 0 };
        typeMap.set(type, {
          count: existing.count + 1,
          cost: existing.cost + (log.cost || 0),
          driverCost: existing.driverCost + (log.driver_payment_amount || 0),
          companyCost: existing.companyCost + (log.company_cost_amount || 0),
        });
      });
      const byType = Array.from(typeMap.entries())
        .map(([type, data]) => ({ type, ...data }))
        .sort((a, b) => b.cost - a.cost);

      // Group by month
      const monthMap = new Map<string, { count: number; cost: number; driverCost: number; companyCost: number }>();
      logs.forEach(log => {
        const date = new Date(log.service_date);
        const month = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        const existing = monthMap.get(month) || { count: 0, cost: 0, driverCost: 0, companyCost: 0 };
        monthMap.set(month, {
          count: existing.count + 1,
          cost: existing.cost + (log.cost || 0),
          driverCost: existing.driverCost + (log.driver_payment_amount || 0),
          companyCost: existing.companyCost + (log.company_cost_amount || 0),
        });
      });
      const byMonth = Array.from(monthMap.entries())
        .map(([month, data]) => ({ month, ...data }))
        .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());

      // Group by truck/trailer
      const truckMap = new Map<string, { count: number; cost: number; driverCost: number; companyCost: number }>();
      logs.forEach(log => {
        const truck = log.trucks?.unit_number || log.trailers?.unit_number || 'Unknown';
        const existing = truckMap.get(truck) || { count: 0, cost: 0, driverCost: 0, companyCost: 0 };
        truckMap.set(truck, {
          count: existing.count + 1,
          cost: existing.cost + (log.cost || 0),
          driverCost: existing.driverCost + (log.driver_payment_amount || 0),
          companyCost: existing.companyCost + (log.company_cost_amount || 0),
        });
      });
      const byTruck = Array.from(truckMap.entries())
        .map(([truck, data]) => ({ truck, ...data }))
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 10); // Top 10

      // Top expenses
      const topExpenses = logs
        .sort((a, b) => (b.cost || 0) - (a.cost || 0))
        .slice(0, 10)
        .map(log => ({
          log_number: log.log_number,
          type: log.maintenance_type,
          cost: log.cost || 0,
          driverCost: log.driver_payment_amount || 0,
          companyCost: log.company_cost_amount || 0,
          date: log.service_date,
        }));

      setStats({
        totalSpent,
        partsSpent,
        laborSpent,
        driverCost,
        companyCost,
        totalLogs,
        avgCostPerLog,
        byType,
        byMonth,
        byTruck,
        topExpenses,
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Maintenance Statistics</h2>
      </div>

      {/* Modern Date Range Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Quick Presets */}
          <div className="flex-1">
            <label className="block text-sm font-semibold text-slate-700 mb-3">Quick Date Range</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <button
                onClick={() => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                  setDateRange({
                    start: firstDay.toISOString().split('T')[0],
                    end: today.toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium border border-blue-200"
              >
                This Month
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                  setDateRange({
                    start: lastMonth.toISOString().split('T')[0],
                    end: lastMonthEnd.toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium border border-purple-200"
              >
                Last Month
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), 0, 1);
                  setDateRange({
                    start: firstDay.toISOString().split('T')[0],
                    end: today.toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium border border-green-200"
              >
                This Year
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const lastYear = new Date(today.getFullYear() - 1, 0, 1);
                  const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
                  setDateRange({
                    start: lastYear.toISOString().split('T')[0],
                    end: lastYearEnd.toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors text-sm font-medium border border-orange-200"
              >
                Last Year
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const last30 = new Date(today);
                  last30.setDate(last30.getDate() - 30);
                  setDateRange({
                    start: last30.toISOString().split('T')[0],
                    end: today.toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 bg-cyan-50 text-cyan-700 rounded-lg hover:bg-cyan-100 transition-colors text-sm font-medium border border-cyan-200"
              >
                Last 30 Days
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const last90 = new Date(today);
                  last90.setDate(last90.getDate() - 90);
                  setDateRange({
                    start: last90.toISOString().split('T')[0],
                    end: today.toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium border border-indigo-200"
              >
                Last 90 Days
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const last6Months = new Date(today);
                  last6Months.setMonth(last6Months.getMonth() - 6);
                  setDateRange({
                    start: last6Months.toISOString().split('T')[0],
                    end: today.toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors text-sm font-medium border border-pink-200"
              >
                Last 6 Months
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const allTime = new Date(2020, 0, 1);
                  setDateRange({
                    start: allTime.toISOString().split('T')[0],
                    end: today.toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 bg-slate-50 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors text-sm font-medium border border-slate-200"
              >
                All Time
              </button>
            </div>
          </div>

          {/* Custom Date Range */}
          <div className="lg:border-l lg:border-slate-200 lg:pl-6">
            <label className="block text-sm font-semibold text-slate-700 mb-3">Custom Date Range</label>
            <div className="flex flex-col gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">From Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">To Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2 mt-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                Showing: {new Date(dateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(dateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">Total</span>
          </div>
          <div className="text-3xl font-bold mb-1">${stats.totalSpent.toLocaleString()}</div>
          <div className="text-sm opacity-90">Total Spent</div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">Driver</span>
          </div>
          <div className="text-3xl font-bold mb-1">${stats.driverCost.toLocaleString()}</div>
          <div className="text-sm opacity-90">Driver Paid</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">Company</span>
          </div>
          <div className="text-3xl font-bold mb-1">${stats.companyCost.toLocaleString()}</div>
          <div className="text-sm opacity-90">Company Paid</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">Parts</span>
          </div>
          <div className="text-3xl font-bold mb-1">${stats.partsSpent.toLocaleString()}</div>
          <div className="text-sm opacity-90">Parts Cost</div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Wrench className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">Labor</span>
          </div>
          <div className="text-3xl font-bold mb-1">${stats.laborSpent.toLocaleString()}</div>
          <div className="text-sm opacity-90">Labor Cost</div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">Count</span>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.totalLogs}</div>
          <div className="text-sm opacity-90">Maintenance Logs</div>
        </div>

        <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 opacity-80" />
            <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">Avg</span>
          </div>
          <div className="text-3xl font-bold mb-1">${stats.avgCostPerLog.toFixed(0)}</div>
          <div className="text-sm opacity-90">Avg per Log</div>
        </div>
      </div>

      {/* No Data Message */}
      {stats.totalLogs === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold text-yellow-900 mb-2">No Maintenance Data Found</h3>
              <p className="text-sm text-yellow-800 mb-3">
                There are no maintenance logs in the selected date range ({new Date(dateRange.start).toLocaleDateString()} - {new Date(dateRange.end).toLocaleDateString()}).
              </p>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• Try adjusting the date range above to include earlier dates</li>
                <li>• Or add maintenance logs from the Maintenance → Logs page</li>
                <li>• Check if you have the correct permissions to view maintenance data</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Charts Grid */}
      {stats.totalLogs > 0 && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Maintenance Type */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-slate-800">Cost by Maintenance Type</h3>
          </div>
          <div className="space-y-3">
            {stats.byType.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{item.type}</span>
                    <span className="text-sm font-bold text-slate-900">${item.cost.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${(item.cost / stats.totalSpent) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-slate-500">{item.count} logs</span>
                    <div className="flex gap-2">
                      {item.driverCost > 0 && <span className="text-orange-600">Driver: ${item.driverCost.toLocaleString()}</span>}
                      {item.companyCost > 0 && <span className="text-purple-600">Co: ${item.companyCost.toLocaleString()}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By Month */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-bold text-slate-800">Monthly Spending</h3>
          </div>
          <div className="space-y-3">
            {stats.byMonth.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{item.month}</span>
                    <span className="text-sm font-bold text-slate-900">${item.cost.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{ width: `${(item.cost / Math.max(...stats.byMonth.map(m => m.cost))) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-slate-500">{item.count} logs</span>
                    <div className="flex gap-2">
                      {item.driverCost > 0 && <span className="text-orange-600">Driver: ${item.driverCost.toLocaleString()}</span>}
                      {item.companyCost > 0 && <span className="text-purple-600">Co: ${item.companyCost.toLocaleString()}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Units by Cost */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Truck className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-bold text-slate-800">Top 10 Units by Cost</h3>
          </div>
          <div className="space-y-3">
            {stats.byTruck.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">#{index + 1} {item.truck}</span>
                    <span className="text-sm font-bold text-slate-900">${item.cost.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-orange-600 h-2 rounded-full transition-all"
                      style={{ width: `${(item.cost / stats.byTruck[0]?.cost || 1) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-slate-500">{item.count} logs</span>
                    <div className="flex gap-2">
                      {item.driverCost > 0 && <span className="text-orange-600">Driver: ${item.driverCost.toLocaleString()}</span>}
                      {item.companyCost > 0 && <span className="text-purple-600">Co: ${item.companyCost.toLocaleString()}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Expenses */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-bold text-slate-800">Top 10 Expenses</h3>
          </div>
          <div className="space-y-2">
            {stats.topExpenses.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">#{index + 1}</span>
                    <span className="text-sm font-medium text-blue-600">{item.log_number}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">{item.type}</div>
                  <div className="text-xs text-slate-500">{new Date(item.date).toLocaleDateString()}</div>
                  {(item.driverCost > 0 || item.companyCost > 0) && (
                    <div className="flex gap-2 mt-1">
                      {item.driverCost > 0 && <span className="text-xs text-orange-600">Driver: ${item.driverCost.toLocaleString()}</span>}
                      {item.companyCost > 0 && <span className="text-xs text-purple-600">Company: ${item.companyCost.toLocaleString()}</span>}
                    </div>
                  )}
                </div>
                <div className="text-lg font-bold text-red-600">${item.cost.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
